// Client for THPE data service: presigned upload to S3, images/videos; upload() supports optional AbortSignal.
import { IFile, IImage, IVideo } from "api/interfaces/data.interface";
import axios from "axios";
import { getAccessToken } from "helpers/Auth.helper";
import * as ImageHelper from "helpers/Image.helper";

const DATA_API_URL = process.env.REACT_APP_DATA_URL;

const FILE_BASE_URL = "/file";
const IMAGE_BASE_URL = "/image";
const VIDEO_BASE_URL = "/video";
const VIDEO_LOG_BASE_URL = "/video-log";

export interface ImageInfo {
    id: string;
    name: string;
    size: number;
    width: number;
    height: number;
    subUrl: string;
}

export interface VideoInfo {
    id: string;
    name: string;
    size: number;
    subUrl: string;
}

export interface FileInfo {
    id: string;
    name: string;
    size: number;
    subUrl: string;
}

const getAxios = () => {
    const headers: any = {
        accesskey: process.env.REACT_APP_DATA_ACCESS_KEY,
        secretkey: process.env.REACT_APP_DATA_SECRET_KEY,
    };

    const accessToken = getAccessToken();
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return axios.create({ headers });
};

interface IUploadResult {
    id: string;
    uploadUrl: string;
    key: string;
    thumbnailImageId?: string;
}

// 공통 파일 업로드
export const uploadFile = async (file: File, isPublic = true): Promise<IUploadResult> => {
    console.log("Data.service:uploadFile");
    console.log({ file, isPublic });
    const params = {
        isPublic,
        name: file.name,
        size: file.size,
    };
    const result = await upload(params, file, false, false);
    console.log({ result });
    return result;
};

// 공통 이미지 업로드
export const uploadImage = async (file: File, isPublic = true): Promise<IUploadResult> => {
    console.log("Data.service:uploadImage");
    console.log({ file, isPublic });

    try {
        // 파일 유효성 검사
        if (!file || !(file instanceof File)) {
            throw new Error('유효하지 않은 파일입니다.');
        }

        // 파일 타입 검사
        if (!file.type.startsWith('image/')) {
            throw new Error('이미지 파일만 업로드할 수 있습니다.');
        }

        // 파일 크기 제한 (10MB)
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        if (file.size > MAX_FILE_SIZE) {
            throw new Error('파일 크기가 너무 큽니다. (최대 10MB)');
        }

        const params: any = await getImageFileInfo(file);
        params["isPublic"] = isPublic;
        const result = await upload(params, file, true, false);
        console.log({ result });
        return result;
    } catch (error) {
        console.error('이미지 업로드 실패:', error);
        throw error;
    }
};


// 공통 비디오 업로드
export const uploadVideo = async (
    file: File,
    isPublic = true,
    onUploadProgress?: (progressEvent: any) => void,
    signal?: AbortSignal
): Promise<IUploadResult> => {
    console.log("Data.service:uploadVideo");
    const params: any = await getVideoFileInfo(file);

    params["isPublic"] = isPublic;

    const result = await upload(params, file, false, true, onUploadProgress, signal);
    console.log({ result });
    return result;
};

// 비디오 썸네일 이미지 변경
export const updateVideoThumbnail = async (
    videoId: string,
    thumbnailImageId: string
) => {
    const url = `${VIDEO_BASE_URL}/${videoId}/thumbnail`;
    const axios = getAxios();
    const result = await axios.patch(`${DATA_API_URL}${url}`, {
        thumbnailImageId,
    });
    return result.data?.data ?? result.data;
};

// 공통 비디오 업로드 (썸네일 포함)
export const uploadVideoWithThumbnail = async (file: File, width: number, height: number, isPublic = true, thumbnailImageId?: string, needConvert: boolean = false, onUploadProgress?: (progressEvent: any) => void): Promise<IUploadResult> => {
    console.log("Data.service:uploadVideoWithThumbnail");
    const params: any = await getVideoFileInfo(file);

    params["isPublic"] = isPublic;
    params["width"] = width;
    params["height"] = height;
    params["needConvert"] = needConvert;

    // thumbnailImageId가 있으면 기존 썸네일 사용, 없으면 비디오에서 추출
    if (thumbnailImageId) {
        params["thumbnailImageId"] = thumbnailImageId;
        console.log("기존 썸네일 이미지 ID 사용:", thumbnailImageId);
    } else {
        // 썸네일 이미지 추출 및 업로드
        try {
            console.log("썸네일 추출 시작");
            const thumbnailBlob = await extractVideoThumbnail(file);
            if (thumbnailBlob) {
                console.log("썸네일 추출 성공, 이미지 업로드 시작");
                const thumbnailFile = new File([thumbnailBlob], `thumbnail_${file.name}.jpg`, { type: 'image/jpeg' });
                // const thumbnailResult = await uploadImage(thumbnailFile, isPublic);
                const thumbnailResult = await uploadImage(thumbnailFile, true);
                if (thumbnailResult?.id) {
                    params["thumbnailImageId"] = thumbnailResult.id;
                    console.log("썸네일 이미지 업로드 성공:", thumbnailResult.id);
                } else {
                    console.warn("썸네일 이미지 업로드 실패: 응답에 id가 없음");
                }
            } else {
                console.warn("썸네일 추출 실패: blob이 null");
            }
        } catch (error) {
            console.warn("썸네일 추출/업로드 실패:", error);
            // 썸네일 실패 시에도 비디오 업로드는 계속 진행
        }
    }

    const result = await upload(params, file, false, true, onUploadProgress, undefined);

    // 썸네일 이미지 ID가 있으면 결과에 추가
    if (result && params.thumbnailImageId) {
        result.thumbnailImageId = params.thumbnailImageId;
    }

    console.log({ result });
    return result;
};

// 비디오 재생 이벤트 로그 생성
export const createVideoLog = async (params: {
    videoId: string;
    sessionId: string;
    eventType: string;
    currentTime: number;
}) => {
    const url = `${VIDEO_LOG_BASE_URL}`;
    const axios = getAxios();
    const infoResult = await axios.post(`${DATA_API_URL}${url}`, params);
    return infoResult.data.data;
};

const upload = async (
    params: any,
    file: File,
    isImage: boolean,
    isVideo: boolean,
    onUploadProgress?: (progressEvent: any) => void,
    signal?: AbortSignal
) => {
    console.log("Data.service:upload");
    const url = isImage ? IMAGE_BASE_URL : isVideo ? VIDEO_BASE_URL : FILE_BASE_URL;

    try {
        console.log({ params });
        const axios = getAxios();

        // axios의 요청 타임아웃을 10분(600000ms)으로 설정합니다.
        const axiosConfig: Parameters<typeof axios.post>[2] = {
            timeout: 60000, // 60초
            ...(signal ? { signal } : {}),
        };
        console.log("axios timeout config:", axiosConfig);

        const infoResult = await axios.post(`${DATA_API_URL}${url}`, params, axiosConfig);
        console.log({ infoResult });
        if (infoResult.status !== 200) {
            throw new Error(`Server response error: ${infoResult.status}`);
        }

        if (!infoResult.data?.data?.uploadUrl) {
            throw new Error('Failed to receive upload URL.');
        }

        const uploadResultStatus = await putFile(
            infoResult.data.data.uploadUrl,
            file,
            onUploadProgress,
            signal
        );
        if (
            !uploadResultStatus ||
            uploadResultStatus < 200 ||
            uploadResultStatus >= 300
        ) {
            throw new Error(`S3 upload failed: ${uploadResultStatus}`);
        }

        return infoResult.data.data;
    } catch (error: any) {
        console.error('Upload failed:', error);

        if (axios.isCancel(error)) {
            throw error;
        }

        // User-friendly error messages in English
        if (error.code === 'ECONNABORTED') {
            throw new Error('Upload timed out. Please check your network connection.');
        } else if (error.message?.includes('Network Error')) {
            throw new Error('A network error occurred. Please check your internet connection.');
        } else if (error.message?.includes('401')) {
            throw new Error('Authentication required. Please log in again.');
        } else if (error.message?.includes('413')) {
            throw new Error('The file is too large.');
        } else {
            throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
        }
    }
};

// get image info
export const getImageInfo = async (id: string) => await getDateInfo(id, true);

// get file info
export const getFileInfo = async (id: string) => await getDateInfo(id, false);

const getDateInfo = async (id: string, isImage: boolean) => {
    const url = `${isImage ? IMAGE_BASE_URL : FILE_BASE_URL}/${id}`;
    return getAxios()
        .get(`${DATA_API_URL}${url}`)
        .then(({ status, data }) => {
            return { status, data };
        })
        .catch(errorHandler);
};

// make image activate
export const makeImageActivate = async (id: string) => await makeDataActivate(id, true);

// make file activate
export const makeFileActivate = async (id: string) => await makeDataActivate(id, false);

// make data activate
const makeDataActivate = async (id: string, isImage: boolean) => {
    const url = `${isImage ? IMAGE_BASE_URL : FILE_BASE_URL}/${id}/activate`;

    return getAxios()
        .patch(`${DATA_API_URL}${url}`)
        .then(({ status, data }) => {
            return { status, data };
        })
        .catch(errorHandler);
};

export const makeImageCopy = async (params: any) => {
    const url = `${IMAGE_BASE_URL}/copy`;

    const axios = getAxios();
    const infoResult = await axios.post(`${DATA_API_URL}${url}`, params);
    if (infoResult.status !== 200) {
        alert(infoResult.data.message);
        return;
    }

    return infoResult.data.data;
};

// get image presgined url
export const getImagePresignedUrl = async (id: string) => await getDataPresignedUrl(id, true, false);

// get file presgined url
export const getFilePresignedUrl = async (id: string) => await getDataPresignedUrl(id, false, false);

// get video presgined url

export interface IVideoPresignedUrlResponse {
    result: string;
}
export const getVideoPresignedUrl = async (id: string): Promise<IVideoPresignedUrlResponse> => await getDataPresignedUrl(id, false, true);

// get data presgined url
export const getDataPresignedUrl = async (id: string, isImage: boolean, isVideo: boolean) => {
    //const url = `${isImage ? IMAGE_BASE_URL : FILE_BASE_URL}/${id}/presigned-url`;
    const url = `${isImage ? IMAGE_BASE_URL : isVideo ? VIDEO_BASE_URL : FILE_BASE_URL}/${id}/presigned-url`;

    return getAxios()
        .get(`${DATA_API_URL}${url}`)
        .then(({ status, data }) => {
            if (status === 200) {
                return data.data;
            }
        })
        .catch(errorHandler);
};

// 파일 다운로드
export const fileDownload = async (url: string, fileName = "") => {
    console.log(url, fileName);
    const fileUrl = url;

    const _axios = axios.create();
    _axios // AxiosInstance
        .get(fileUrl, { responseType: "blob" })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || fileUrl.split("/").pop() || "download";
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        })
        .catch((error) => {
            console.error("파일 다운로드 오류:", error);
        });
};

export const getWaterMarkImage = async (id: string) => {
    const url = `${IMAGE_BASE_URL}/${id}/water-mark`;

    return getAxios()
        .get(`${DATA_API_URL}${url}`)
        .then(({ status, data }) => {
            if (status === 200) {
                return data.data;
            }
        })
        .catch(errorHandler);
};

const getImageFileInfo: any = async (file: File) => {
    return new Promise((resolve, reject) => {
        // 파일 크기 제한 체크 (10MB)
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        if (file.size > MAX_FILE_SIZE) {
            console.warn('파일 크기가 너무 큽니다:', file.size);
            resolve({
                name: file.name,
                size: file.size,
                width: 0,
                height: 0,
            });
            return;
        }

        // 안드로이드 15에서 더 안정적인 방법으로 이미지 크기 추출
        const extractImageDimensions = async (file: File) => {
            try {
                console.log('Starting image dimension extraction for file:', {
                    name: file.name,
                    size: file.size,
                    type: file.type
                });

                for (const fn of [ImageHelper.viaCreateImageBitmap, ImageHelper.viaImageDecoder, ImageHelper.viaImageDecode, ImageHelper.viaCanvas]) {
                    const r = await fn(file);
                    if (r.width > 0 && r.height > 0) return r;
                }
                const type = await ImageHelper.sniffType(file);
                return { width: 0, height: 0, method: 'failed', typeHint: type };

                // // 방법 1: EXIF 데이터에서 크기 추출 (가장 안정적)
                // const result1 = await tryEXIFMethod(file);
                // if (result1.width > 0 && result1.height > 0) {
                //     console.log('EXIF method succeeded:', result1);
                //     return result1;
                // }

                // // 방법 2: FileReader + Image 조합 (안드로이드 15 최적화)
                // const result2 = await tryFileReaderImageMethod(file);
                // if (result2.width > 0 && result2.height > 0) {
                //     console.log('FileReader Image method succeeded:', result2);
                //     return result2;
                // }

                // // 방법 3: URL.createObjectURL + Image (fallback)
                // const result3 = await tryObjectURLMethod(file);
                // if (result3.width > 0 && result3.height > 0) {
                //     console.log('ObjectURL method succeeded:', result3);
                //     return result3;
                // }

                // // 방법 4: Canvas를 사용한 직접 추출
                // const result4 = await tryCanvasDirectMethod(file);
                // if (result4.width > 0 && result4.height > 0) {
                //     console.log('Canvas direct method succeeded:', result4);
                //     return result4;
                // }

                // // 방법 5: ArrayBuffer를 통한 바이너리 분석
                // const result5 = await tryArrayBufferMethod(file);
                // if (result5.width > 0 && result5.height > 0) {
                //     console.log('ArrayBuffer method succeeded:', result5);
                //     return result5;
                // }

                // console.warn('모든 이미지 크기 추출 방법 실패');
                // return { width: 0, height: 0 };
            } catch (error) {
                console.warn('이미지 크기 추출 오류:', error);
                return { width: 0, height: 0 };
            }
        };

        extractImageDimensions(file)
            .then(({ width, height }) => {
                resolve({
                    name: file.name,
                    size: file.size,
                    width: width,
                    height: height,
                });
            })
            .catch(error => {
                console.warn('이미지 정보 추출 최종 실패:', error);
                resolve({
                    name: file.name,
                    size: file.size,
                    width: 0,
                    height: 0,
                });
            });
    });
};

// 방법 1: FileReader + Image 조합 (안드로이드 15 최적화)
const tryFileReaderImageMethod = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            const img = new Image();

            // 안드로이드 15 최적화 설정
            img.crossOrigin = 'anonymous';
            img.setAttribute('crossorigin', 'anonymous');
            img.setAttribute('decoding', 'sync'); // 동기 디코딩으로 크기 확보
            img.setAttribute('loading', 'eager');

            const timeout = setTimeout(() => {
                console.warn('FileReader Image method: Timeout after 5 seconds');
                resolve({ width: 0, height: 0 });
            }, 5000);

            img.onload = () => {
                clearTimeout(timeout);
                try {
                    // 디버깅을 위한 상세 로그
                    console.log('FileReader Image method - Image loaded:', {
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight,
                        width: img.width,
                        height: img.height,
                        complete: img.complete
                    });

                    // 여러 방법으로 크기 추출 시도
                    let width = 0;
                    let height = 0;

                    // 방법 1: naturalWidth/naturalHeight
                    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                        width = img.naturalWidth;
                        height = img.naturalHeight;
                        console.log('Using naturalWidth/naturalHeight:', { width, height });
                    }
                    // 방법 2: width/height
                    else if (img.width > 0 && img.height > 0) {
                        width = img.width;
                        height = img.height;
                        console.log('Using width/height:', { width, height });
                    }
                    // 방법 3: 이미지가 완전히 로드될 때까지 대기
                    else {
                        // 추가 대기 후 다시 시도
                        setTimeout(() => {
                            const retryWidth = img.naturalWidth || img.width || 0;
                            const retryHeight = img.naturalHeight || img.height || 0;
                            console.log('Retry after delay:', { retryWidth, retryHeight });

                            if (retryWidth > 0 && retryHeight > 0) {
                                resolve({ width: retryWidth, height: retryHeight });
                            } else {
                                resolve({ width: 0, height: 0 });
                            }
                        }, 100);
                        return;
                    }

                    if (width > 0 && height > 0) {
                        console.log('FileReader Image method success:', { width, height });
                        resolve({ width, height });
                    } else {
                        console.warn('FileReader Image method: Invalid dimensions after all attempts');
                        resolve({ width: 0, height: 0 });
                    }
                } catch (error) {
                    console.warn('FileReader Image method error:', error);
                    resolve({ width: 0, height: 0 });
                }
            };

            img.onerror = (error) => {
                clearTimeout(timeout);
                console.warn('FileReader Image method: Load failed', error);
                resolve({ width: 0, height: 0 });
            };

            // 이미지 로드 시작
            img.src = dataUrl;
        };

        reader.onerror = (error) => {
            console.warn('FileReader Image method: Read failed', error);
            resolve({ width: 0, height: 0 });
        };

        reader.readAsDataURL(file);
    });
};

// 방법 2: URL.createObjectURL + Image (fallback)
const tryObjectURLMethod = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        try {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();

            img.crossOrigin = 'anonymous';
            img.setAttribute('crossorigin', 'anonymous');

            const timeout = setTimeout(() => {
                URL.revokeObjectURL(objectUrl);
                resolve({ width: 0, height: 0 });
            }, 3000);

            img.onload = () => {
                clearTimeout(timeout);
                URL.revokeObjectURL(objectUrl);

                try {
                    const width = img.naturalWidth || img.width || 0;
                    const height = img.naturalHeight || img.height || 0;

                    if (width > 0 && height > 0) {
                        console.log('ObjectURL method success:', { width, height });
                        resolve({ width, height });
                    } else {
                        console.warn('ObjectURL method: Invalid dimensions');
                        resolve({ width: 0, height: 0 });
                    }
                } catch (error) {
                    console.warn('ObjectURL method error:', error);
                    resolve({ width: 0, height: 0 });
                }
            };

            img.onerror = () => {
                clearTimeout(timeout);
                URL.revokeObjectURL(objectUrl);
                console.warn('ObjectURL method: Load failed');
                resolve({ width: 0, height: 0 });
            };

            img.src = objectUrl;
        } catch (error) {
            console.warn('ObjectURL method init error:', error);
            resolve({ width: 0, height: 0 });
        }
    });
};

// 방법 3: Canvas를 사용한 직접 추출
const tryCanvasDirectMethod = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        try {
            const reader = new FileReader();

            reader.onload = (e) => {
                const dataUrl = e.target?.result as string;
                const img = new Image();

                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        if (!ctx) {
                            resolve({ width: 0, height: 0 });
                            return;
                        }

                        // Canvas에 이미지 그리기
                        canvas.width = img.naturalWidth || img.width || 1;
                        canvas.height = img.naturalHeight || img.height || 1;
                        ctx.drawImage(img, 0, 0);

                        const width = canvas.width;
                        const height = canvas.height;

                        if (width > 0 && height > 0) {
                            console.log('Canvas direct method success:', { width, height });
                            resolve({ width, height });
                        } else {
                            console.warn('Canvas direct method: Invalid dimensions');
                            resolve({ width: 0, height: 0 });
                        }
                    } catch (error) {
                        console.warn('Canvas direct method error:', error);
                        resolve({ width: 0, height: 0 });
                    }
                };

                img.onerror = () => {
                    console.warn('Canvas direct method: Image load failed');
                    resolve({ width: 0, height: 0 });
                };

                img.src = dataUrl;
            };

            reader.onerror = () => {
                console.warn('Canvas direct method: FileReader failed');
                resolve({ width: 0, height: 0 });
            };

            reader.readAsDataURL(file);
        } catch (error) {
            console.warn('Canvas direct method init error:', error);
            resolve({ width: 0, height: 0 });
        }
    });
};

// 방법 0: EXIF 데이터에서 크기 추출 (가장 안정적)
const tryEXIFMethod = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        try {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    const uint8Array = new Uint8Array(arrayBuffer);

                    // JPEG 파일 시그니처 확인
                    if (uint8Array[0] === 0xFF && uint8Array[1] === 0xD8) {
                        const dimensions = extractJPEGDimensions(uint8Array);
                        if (dimensions.width > 0 && dimensions.height > 0) {
                            console.log('EXIF method success:', dimensions);
                            resolve(dimensions);
                            return;
                        }
                    }

                    // PNG 파일 시그니처 확인
                    if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4E && uint8Array[3] === 0x47) {
                        const dimensions = extractPNGDimensions(uint8Array);
                        if (dimensions.width > 0 && dimensions.height > 0) {
                            console.log('EXIF method success (PNG):', dimensions);
                            resolve(dimensions);
                            return;
                        }
                    }

                    console.warn('EXIF method: Unsupported file format or no dimensions found');
                    resolve({ width: 0, height: 0 });
                } catch (error) {
                    console.warn('EXIF method error:', error);
                    resolve({ width: 0, height: 0 });
                }
            };

            reader.onerror = () => {
                console.warn('EXIF method: FileReader failed');
                resolve({ width: 0, height: 0 });
            };

            reader.readAsArrayBuffer(file);
        } catch (error) {
            console.warn('EXIF method init error:', error);
            resolve({ width: 0, height: 0 });
        }
    });
};

// JPEG 이미지 크기 추출
const extractJPEGDimensions = (uint8Array: Uint8Array): { width: number, height: number } => {
    try {
        let i = 2; // JPEG 시그니처 이후부터 시작

        while (i < uint8Array.length - 1) {
            // SOF (Start of Frame) 마커 찾기
            if (uint8Array[i] === 0xFF && (uint8Array[i + 1] === 0xC0 || uint8Array[i + 1] === 0xC2)) {
                // 높이 (2바이트, 빅엔디안)
                const height = (uint8Array[i + 5] << 8) | uint8Array[i + 6];
                // 너비 (2바이트, 빅엔디안)
                const width = (uint8Array[i + 7] << 8) | uint8Array[i + 8];

                if (width > 0 && height > 0 && width < 10000 && height < 10000) {
                    return { width, height };
                }
            }
            i++;
        }

        return { width: 0, height: 0 };
    } catch (error) {
        console.warn('JPEG dimension extraction error:', error);
        return { width: 0, height: 0 };
    }
};

// PNG 이미지 크기 추출
const extractPNGDimensions = (uint8Array: Uint8Array): { width: number, height: number } => {
    try {
        // PNG IHDR 청크는 8바이트 시그니처 이후 4바이트 청크 길이, 4바이트 청크 타입, 13바이트 데이터
        // IHDR 데이터: width(4) + height(4) + bit_depth(1) + color_type(1) + compression(1) + filter(1) + interlace(1)
        if (uint8Array.length >= 24) {
            // IHDR 청크 확인
            if (uint8Array[12] === 0x49 && uint8Array[13] === 0x48 && uint8Array[14] === 0x44 && uint8Array[15] === 0x52) {
                // 너비 (4바이트, 빅엔디안)
                const width = (uint8Array[16] << 24) | (uint8Array[17] << 16) | (uint8Array[18] << 8) | uint8Array[19];
                // 높이 (4바이트, 빅엔디안)
                const height = (uint8Array[20] << 24) | (uint8Array[21] << 16) | (uint8Array[22] << 8) | uint8Array[23];

                if (width > 0 && height > 0 && width < 10000 && height < 10000) {
                    return { width, height };
                }
            }
        }

        return { width: 0, height: 0 };
    } catch (error) {
        console.warn('PNG dimension extraction error:', error);
        return { width: 0, height: 0 };
    }
};

// 방법 5: ArrayBuffer를 통한 바이너리 분석
const tryArrayBufferMethod = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        try {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    const uint8Array = new Uint8Array(arrayBuffer);

                    // 파일 타입별 크기 추출
                    let dimensions = { width: 0, height: 0 };

                    if (file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg')) {
                        dimensions = extractJPEGDimensions(uint8Array);
                    } else if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
                        dimensions = extractPNGDimensions(uint8Array);
                    } else if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) {
                        dimensions = extractGIFDimensions(uint8Array);
                    } else if (file.type === 'image/webp' || file.name.toLowerCase().endsWith('.webp')) {
                        dimensions = extractWebPDimensions(uint8Array);
                    }

                    if (dimensions.width > 0 && dimensions.height > 0) {
                        console.log('ArrayBuffer method success:', dimensions);
                        resolve(dimensions);
                    } else {
                        console.warn('ArrayBuffer method: No dimensions found');
                        resolve({ width: 0, height: 0 });
                    }
                } catch (error) {
                    console.warn('ArrayBuffer method error:', error);
                    resolve({ width: 0, height: 0 });
                }
            };

            reader.onerror = () => {
                console.warn('ArrayBuffer method: FileReader failed');
                resolve({ width: 0, height: 0 });
            };

            reader.readAsArrayBuffer(file);
        } catch (error) {
            console.warn('ArrayBuffer method init error:', error);
            resolve({ width: 0, height: 0 });
        }
    });
};

// GIF 이미지 크기 추출
const extractGIFDimensions = (uint8Array: Uint8Array): { width: number, height: number } => {
    try {
        // GIF 시그니처 확인
        if (uint8Array[0] === 0x47 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46) {
            // 너비 (2바이트, 리틀엔디안)
            const width = uint8Array[6] | (uint8Array[7] << 8);
            // 높이 (2바이트, 리틀엔디안)
            const height = uint8Array[8] | (uint8Array[9] << 8);

            if (width > 0 && height > 0 && width < 10000 && height < 10000) {
                return { width, height };
            }
        }

        return { width: 0, height: 0 };
    } catch (error) {
        console.warn('GIF dimension extraction error:', error);
        return { width: 0, height: 0 };
    }
};

// WebP 이미지 크기 추출
const extractWebPDimensions = (uint8Array: Uint8Array): { width: number, height: number } => {
    try {
        // WebP 시그니처 확인
        if (uint8Array[0] === 0x52 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46 && uint8Array[3] === 0x46 &&
            uint8Array[8] === 0x57 && uint8Array[9] === 0x45 && uint8Array[10] === 0x42 && uint8Array[11] === 0x50) {

            // VP8/VP8L 헤더에서 크기 추출
            if (uint8Array[12] === 0x56 && uint8Array[13] === 0x50 && uint8Array[14] === 0x38) {
                // VP8 포맷
                const width = (uint8Array[26] | (uint8Array[27] << 8)) & 0x3FFF;
                const height = (uint8Array[28] | (uint8Array[29] << 8)) & 0x3FFF;

                if (width > 0 && height > 0 && width < 10000 && height < 10000) {
                    return { width, height };
                }
            } else if (uint8Array[12] === 0x56 && uint8Array[13] === 0x50 && uint8Array[14] === 0x38 && uint8Array[15] === 0x4C) {
                // VP8L 포맷
                const bits = (uint8Array[21] << 16) | (uint8Array[20] << 8) | uint8Array[19];
                const width = (bits & 0x3FFF) + 1;
                const height = ((bits >> 14) & 0x3FFF) + 1;

                if (width > 0 && height > 0 && width < 10000 && height < 10000) {
                    return { width, height };
                }
            }
        }

        return { width: 0, height: 0 };
    } catch (error) {
        console.warn('WebP dimension extraction error:', error);
        return { width: 0, height: 0 };
    }
};

const getVideoFileInfo: any = async (file: File) => {
    return {
        name: file.name,
        size: file.size,
    };
};
const addImageProcess: any = (src: string) => {
    return new Promise((resolve, reject) => {
        // 안드로이드 15에서 이미지 크기 추출이 실패하는 경우를 위한 다중 시도 로직
        const tryMultipleMethods = async () => {
            // 방법 1: 일반 Image 객체 사용
            const result1 = await tryImageMethod(src);
            if (result1.width > 0 && result1.height > 0) {
                return result1;
            }

            // 방법 2: Canvas를 사용한 크기 추출
            const result2 = await tryCanvasMethod(src);
            if (result2.width > 0 && result2.height > 0) {
                return result2;
            }

            // 방법 3: FileReader + Canvas 조합
            const result3 = await tryFileReaderCanvasMethod(src);
            if (result3.width > 0 && result3.height > 0) {
                return result3;
            }

            console.warn('모든 이미지 크기 추출 방법 실패');
            return { width: 0, height: 0 };
        };

        tryMultipleMethods()
            .then(resolve)
            .catch(error => {
                console.warn('이미지 크기 추출 최종 실패:', error);
                resolve({ width: 0, height: 0 });
            });
    });
};

// 방법 1: 일반 Image 객체 사용
const tryImageMethod = (src: string): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        const img = new Image();

        // 안드로이드 15 호환성을 위한 설정
        img.crossOrigin = 'anonymous';
        img.setAttribute('crossorigin', 'anonymous');
        img.setAttribute('decoding', 'async');
        img.setAttribute('loading', 'eager');

        const timeout = setTimeout(() => {
            img.onload = null;
            img.onerror = null;
            console.warn('Image method timeout');
            resolve({ width: 0, height: 0 });
        }, 5000);

        img.onload = () => {
            clearTimeout(timeout);
            try {
                if (img.width > 0 && img.height > 0) {
                    console.log('Image method success:', { width: img.width, height: img.height });
                    resolve({ width: img.width, height: img.height });
                } else {
                    console.warn('Image method: Invalid dimensions');
                    resolve({ width: 0, height: 0 });
                }
            } catch (error) {
                console.warn('Image method error:', error);
                resolve({ width: 0, height: 0 });
            }
        };

        img.onerror = () => {
            clearTimeout(timeout);
            console.warn('Image method: Load failed');
            resolve({ width: 0, height: 0 });
        };

        img.src = src;
    });
};

// 방법 2: Canvas를 사용한 크기 추출
const tryCanvasMethod = (src: string): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            const timeout = setTimeout(() => {
                resolve({ width: 0, height: 0 });
            }, 5000);

            img.onload = () => {
                clearTimeout(timeout);
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        resolve({ width: 0, height: 0 });
                        return;
                    }

                    // Canvas에 이미지 그리기 시도
                    canvas.width = 1;
                    canvas.height = 1;
                    ctx.drawImage(img, 0, 0, 1, 1);

                    // 원본 크기 가져오기
                    const width = img.naturalWidth || img.width;
                    const height = img.naturalHeight || img.height;

                    if (width > 0 && height > 0) {
                        console.log('Canvas method success:', { width, height });
                        resolve({ width, height });
                    } else {
                        console.warn('Canvas method: Invalid dimensions');
                        resolve({ width: 0, height: 0 });
                    }
                } catch (error) {
                    console.warn('Canvas method error:', error);
                    resolve({ width: 0, height: 0 });
                }
            };

            img.onerror = () => {
                clearTimeout(timeout);
                console.warn('Canvas method: Load failed');
                resolve({ width: 0, height: 0 });
            };

            img.src = src;
        } catch (error) {
            console.warn('Canvas method init error:', error);
            resolve({ width: 0, height: 0 });
        }
    });
};

// 방법 3: FileReader + Canvas 조합
const tryFileReaderCanvasMethod = (src: string): Promise<{ width: number, height: number }> => {
    return new Promise((resolve) => {
        try {
            if (!src.startsWith('data:')) {
                resolve({ width: 0, height: 0 });
                return;
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';

            const timeout = setTimeout(() => {
                resolve({ width: 0, height: 0 });
            }, 5000);

            img.onload = () => {
                clearTimeout(timeout);
                try {
                    // naturalWidth/naturalHeight 사용 (안드로이드 15에서 더 안정적)
                    const width = img.naturalWidth || img.width || 0;
                    const height = img.naturalHeight || img.height || 0;

                    if (width > 0 && height > 0) {
                        console.log('FileReader Canvas method success:', { width, height });
                        resolve({ width, height });
                    } else {
                        // Canvas로 다시 시도
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        if (ctx) {
                            canvas.width = img.width || 1;
                            canvas.height = img.height || 1;
                            ctx.drawImage(img, 0, 0);

                            const canvasWidth = canvas.width;
                            const canvasHeight = canvas.height;

                            if (canvasWidth > 0 && canvasHeight > 0) {
                                console.log('FileReader Canvas fallback success:', { width: canvasWidth, height: canvasHeight });
                                resolve({ width: canvasWidth, height: canvasHeight });
                            } else {
                                resolve({ width: 0, height: 0 });
                            }
                        } else {
                            resolve({ width: 0, height: 0 });
                        }
                    }
                } catch (error) {
                    console.warn('FileReader Canvas method error:', error);
                    resolve({ width: 0, height: 0 });
                }
            };

            img.onerror = () => {
                clearTimeout(timeout);
                console.warn('FileReader Canvas method: Load failed');
                resolve({ width: 0, height: 0 });
            };

            img.src = src;
        } catch (error) {
            console.warn('FileReader Canvas method init error:', error);
            resolve({ width: 0, height: 0 });
        }
    });
};

// 이미지 압축 함수 (iOS Safari 메모리 부족 방지)
const compressImageDataUrl = async (dataUrl: string, quality: number = 0.8): Promise<string> => {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        resolve(dataUrl);
                        return;
                    }

                    // 최대 크기 제한 (2048px)
                    const maxSize = 2048;
                    let { width, height } = img;

                    if (width > maxSize || height > maxSize) {
                        const ratio = Math.min(maxSize / width, maxSize / height);
                        width *= ratio;
                        height *= ratio;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx.drawImage(img, 0, 0, width, height);

                    const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedDataUrl);
                } catch (error) {
                    console.warn('이미지 압축 실패:', error);
                    resolve(dataUrl);
                }
            };
            img.onerror = () => {
                console.warn('압축용 이미지 로드 실패');
                resolve(dataUrl);
            };
            img.src = dataUrl;
        } catch (error) {
            console.warn('이미지 압축 초기화 실패:', error);
            resolve(dataUrl);
        }
    });
};

// FileReader를 사용한 대체 방법 (개선된 버전)
const tryFileReaderFallback = (src: string, resolve: Function, reject: Function) => {
    try {
        // 네트워크 오류 처리를 위한 타임아웃
        const fetchTimeout = setTimeout(() => {
            console.warn('blob fetch 타임아웃');
            resolve({ width: 0, height: 0 });
        }, 5000);

        // blob URL을 File로 변환
        fetch(src, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then(response => {
                clearTimeout(fetchTimeout);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                return response.blob();
            })
            .then(blob => {
                // 구형 브라우저 FileReader 지원 체크
                if (!window.FileReader) {
                    console.warn('FileReader를 지원하지 않는 브라우저');
                    resolve({ width: 0, height: 0 });
                    return;
                }

                const reader = new FileReader();

                // FileReader 타임아웃 설정
                const readerTimeout = setTimeout(() => {
                    console.warn('FileReader 타임아웃');
                    resolve({ width: 0, height: 0 });
                }, 3000);

                reader.onload = (e) => {
                    clearTimeout(readerTimeout);
                    const img = new Image();

                    img.onload = () => {
                        try {
                            if (img.width > 0 && img.height > 0) {
                                resolve({ width: img.width, height: img.height });
                            } else {
                                console.warn('FileReader 이미지 크기가 유효하지 않음');
                                resolve({ width: 0, height: 0 });
                            }
                        } catch (error) {
                            console.warn('FileReader 이미지 크기 추출 중 오류:', error);
                            resolve({ width: 0, height: 0 });
                        }
                    };

                    img.onerror = () => {
                        clearTimeout(readerTimeout);
                        console.warn('FileReader 이미지 로드 실패');
                        resolve({ width: 0, height: 0 });
                    };

                    img.src = e.target?.result as string;
                };

                reader.onerror = () => {
                    clearTimeout(readerTimeout);
                    console.warn('FileReader 읽기 실패');
                    resolve({ width: 0, height: 0 });
                };

                reader.readAsDataURL(blob);
            })
            .catch(error => {
                clearTimeout(fetchTimeout);
                console.warn('blob fetch 실패:', error);
                resolve({ width: 0, height: 0 });
            });
    } catch (error) {
        console.warn('FileReader fallback 실패:', error);
        resolve({ width: 0, height: 0 });
    }
};

// 비디오에서 썸네일 이미지 추출 (iOS 방식)
const extractVideoThumbnailIOS = async (videoFile: File): Promise<Blob | null> => {
    console.log("Data.service:extractVideoThumbnailIOS");

    return new Promise((resolve) => {
        let video: HTMLVideoElement | null = null;
        let videoUrl: string | null = null;
        let timeoutId: NodeJS.Timeout | null = null;
        let isResolved = false;

        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (videoUrl) {
                URL.revokeObjectURL(videoUrl);
                videoUrl = null;
            }
            if (video) {
                video.src = '';
                video.load();
                video = null;
            }
        };

        const safeResolve = (blob: Blob | null) => {
            if (!isResolved) {
                isResolved = true;
                cleanup();
                resolve(blob);
            }
        };

        try {
            console.log("Data.service:extractVideoThumbnailIOS try");

            // 8초 타임아웃 설정
            timeoutId = setTimeout(() => {
                console.log("Data.service:extractVideoThumbnailIOS timeout");
                safeResolve(null);
            }, 8000);

            video = document.createElement('video');
            video.muted = true;
            video.playsInline = true; // iOS에서 필요
            video.preload = 'metadata';

            // iOS Safari에서는 crossOrigin 설정을 제거하는 것이 안전
            // video.crossOrigin = 'anonymous';

            console.log("Data.service:extractVideoThumbnailIOS onloadedmetadata");
            video.onloadedmetadata = () => {
                try {
                    console.log("Data.service:extractVideoThumbnailIOS metadata loaded");
                    // 비디오의 1/3 지점에서 썸네일 추출
                    const seekTime = video!.duration / 3;
                    video!.currentTime = seekTime;
                } catch (error) {
                    console.warn("Data.service:extractVideoThumbnailIOS onloadedmetadata error:", error);
                    safeResolve(null);
                }
            };

            console.log("Data.service:extractVideoThumbnailIOS onseeked");
            video.onseeked = () => {
                try {
                    console.log("Data.service:extractVideoThumbnailIOS onseeked try");

                    // 비디오 크기 확인
                    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
                        console.log("Data.service:extractVideoThumbnailIOS invalid video dimensions");
                        safeResolve(null);
                        return;
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        // iOS에서 안전한 이미지 그리기
                        try {
                            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        } catch (drawError) {
                            console.warn("Data.service:extractVideoThumbnailIOS drawImage error:", drawError);
                            safeResolve(null);
                            return;
                        }

                        console.log("Data.service:extractVideoThumbnailIOS onseeked canvas.toBlob");
                        canvas.toBlob((blob) => {
                            console.log("Data.service:extractVideoThumbnailIOS onseeked canvas.toBlob blob", blob);
                            safeResolve(blob);
                        }, 'image/jpeg', 0.8);
                    } else {
                        console.log("Data.service:extractVideoThumbnailIOS onseeked canvas.toBlob null");
                        safeResolve(null);
                    }
                } catch (error) {
                    console.warn("Data.service:extractVideoThumbnailIOS onseeked error:", error);
                    safeResolve(null);
                }
            };

            video.onerror = (error) => {
                console.log("Data.service:extractVideoThumbnailIOS onerror", error);
                safeResolve(null);
            };

            video.oncanplay = () => {
                console.log("Data.service:extractVideoThumbnailIOS oncanplay");
                // iOS에서 canplay 이벤트가 더 안정적일 수 있음
                if (video && video.readyState >= 2) { // HAVE_CURRENT_DATA
                    try {
                        const seekTime = video.duration / 3;
                        video.currentTime = seekTime;
                    } catch (error) {
                        console.warn("Data.service:extractVideoThumbnailIOS oncanplay error:", error);
                        safeResolve(null);
                    }
                }
            };

            // 비디오 파일을 URL로 변환
            videoUrl = URL.createObjectURL(videoFile);
            console.log("Data.service:extractVideoThumbnailIOS videoUrl", videoUrl);
            video.src = videoUrl;

        } catch (error) {
            console.log("Data.service:extractVideoThumbnailIOS try error", error);
            console.warn("썸네일 추출 초기화 실패:", error);
            safeResolve(null);
        }
    });
};

// 비디오에서 썸네일 이미지 추출 (Android 방식)
const extractVideoThumbnailAndroid = async (videoFile: File): Promise<Blob | null> => {
    console.log("Data.service:extractVideoThumbnailAndroid");

    return new Promise((resolve) => {
        let video: HTMLVideoElement | null = null;
        let videoUrl: string | null = null;
        let timeoutId: NodeJS.Timeout | null = null;
        let isResolved = false;

        const cleanup = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            if (videoUrl) {
                URL.revokeObjectURL(videoUrl);
                videoUrl = null;
            }
            if (video) {
                video.src = '';
                video.load();
                video = null;
            }
        };

        const safeResolve = (blob: Blob | null) => {
            if (!isResolved) {
                isResolved = true;
                cleanup();
                resolve(blob);
            }
        };

        try {
            console.log("Data.service:extractVideoThumbnailAndroid try");

            // 8초 타임아웃 설정
            timeoutId = setTimeout(() => {
                console.log("Data.service:extractVideoThumbnailAndroid timeout");
                safeResolve(null);
            }, 8000);

            video = document.createElement('video');
            video.muted = true;
            video.crossOrigin = 'anonymous'; // Android에서는 crossOrigin 필요
            video.preload = 'metadata';

            console.log("Data.service:extractVideoThumbnailAndroid onloadedmetadata");
            video.onloadedmetadata = () => {
                try {
                    console.log("Data.service:extractVideoThumbnailAndroid metadata loaded");
                    // 비디오의 1/3 지점에서 썸네일 추출
                    const seekTime = video!.duration / 3;
                    video!.currentTime = seekTime;
                } catch (error) {
                    console.warn("Data.service:extractVideoThumbnailAndroid onloadedmetadata error:", error);
                    safeResolve(null);
                }
            };

            console.log("Data.service:extractVideoThumbnailAndroid onseeked");
            video.onseeked = () => {
                try {
                    console.log("Data.service:extractVideoThumbnailAndroid onseeked try");

                    // 비디오 크기 확인
                    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
                        console.log("Data.service:extractVideoThumbnailAndroid invalid video dimensions");
                        safeResolve(null);
                        return;
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        try {
                            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        } catch (drawError) {
                            console.warn("Data.service:extractVideoThumbnailAndroid drawImage error:", drawError);
                            safeResolve(null);
                            return;
                        }

                        console.log("Data.service:extractVideoThumbnailAndroid onseeked canvas.toBlob");
                        canvas.toBlob((blob) => {
                            console.log("Data.service:extractVideoThumbnailAndroid onseeked canvas.toBlob blob", blob);
                            safeResolve(blob);
                        }, 'image/jpeg', 0.8);
                    } else {
                        console.log("Data.service:extractVideoThumbnailAndroid onseeked canvas.toBlob null");
                        safeResolve(null);
                    }
                } catch (error) {
                    console.warn("Data.service:extractVideoThumbnailAndroid onseeked error:", error);
                    safeResolve(null);
                }
            };

            video.onerror = (error) => {
                console.log("Data.service:extractVideoThumbnailAndroid onerror", error);
                safeResolve(null);
            };

            // 비디오 파일을 URL로 변환
            videoUrl = URL.createObjectURL(videoFile);
            console.log("Data.service:extractVideoThumbnailAndroid videoUrl", videoUrl);
            video.src = videoUrl;

        } catch (error) {
            console.log("Data.service:extractVideoThumbnailAndroid try error", error);
            console.warn("썸네일 추출 초기화 실패:", error);
            safeResolve(null);
        }
    });
};

// 비디오에서 썸네일 이미지 추출 (통합 함수)
const extractVideoThumbnail = async (videoFile: File): Promise<Blob | null> => {
    console.log("Data.service:extractVideoThumbnail - 통합 함수 시작");

    try {
        // 1. 먼저 Android 방식 시도
        console.log("Android 방식으로 썸네일 추출 시도");
        const androidResult = await extractVideoThumbnailAndroid(videoFile);
        if (androidResult) {
            console.log("Android 방식으로 썸네일 추출 성공");
            return androidResult;
        }

        console.log("Android 방식 실패, iOS 방식으로 재시도");
        // 2. Android 방식이 실패하면 iOS 방식 시도
        const iosResult = await extractVideoThumbnailIOS(videoFile);
        if (iosResult) {
            console.log("iOS 방식으로 썸네일 추출 성공");
            return iosResult;
        }

        console.log("모든 방식으로 썸네일 추출 실패");
        return null;

    } catch (error) {
        console.warn("extractVideoThumbnail 통합 함수 에러:", error);
        return null;
    }
};

// S3 put file
const putFile = async (
    signedUrl: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void,
    signal?: AbortSignal
) => {
    console.log({ signedUrl, file });
    const options: Parameters<typeof axios.put>[2] = {
        headers: {
            "Content-Type": file.type,
        },
        timeout: 600000, // 60초 타임아웃
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: onUploadProgress,
        ...(signal ? { signal } : {}),
    };

    const _axios = axios.create();
    return _axios
        .put(signedUrl, file, options)
        .then(({ status, data }) => {
            console.log('S3 업로드 성공:', status);
            return status;
        })
        .catch((error) => {
            console.error('S3 업로드 실패:', error);
            if (axios.isCancel(error)) {
                throw error;
            }
            if (error.code === 'ECONNABORTED') {
                throw new Error('업로드 시간이 초과되었습니다.');
            } else if (error.response?.status === 403) {
                throw new Error('업로드 권한이 없습니다.');
            } else if (error.response?.status === 413) {
                throw new Error('파일 크기가 너무 큽니다.');
            } else {
                throw error;
            }
        });
};

const errorHandler = ({ response }: any) => {
    console.log(response);
    if (response?.status === 400) {
        throw new Error("Bad request");
    }

    if (response?.status === 401) {
        throw new Error("Authentication failed");
    }

    if (response?.status === 403) {
        throw new Error("Access denied");
    }

    if (response?.status === 404) {
        throw new Error("Resource not found");
    }

    if (response?.status >= 500) {
        throw new Error("Server error occurred");
    }

    throw new Error("Unknown error occurred");
};
